import { Button, Flex, Text, Alert } from '@smarterservices/smarter-ui';
import React, { useEffect, useState } from 'react';
import { sendMessageToExtension } from '../../helpers/helpers';
import { putSessionInProgress } from './api';
import { completeAndGetNextStep } from '../../routes/Onboarding/api';
import { updateToolbarButtonState } from '../../helpers/helpers';
import SinglePane from '../SinglePane';
import { useAppStore } from '../../stores/app';
import { usePaneViewer } from '../PaneViewer';
import Loader from '../Common/Loader';
import StepInstructions from '../Common/StepInstructions';

const ExamRedirect = ({ step }) => {
  const [examRedirectUrl, setExamRedirectUrl] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { getSessionStartData, apiClient } = useAppStore();
  const { setHideTitle } = usePaneViewer();

  // To provide some good feedback to the user, this is the amount of time they will see the loader...
  const MIN_REDIRECT_DELAY = 1000;

  useEffect(() => {
    const initLockdownOptions = async () => {
      const sessionStartData = await getSessionStartData();
      const examUrl = sessionStartData.examUrl || null;
      setExamRedirectUrl(examUrl);

      // now if we are auto advancing, call the redirectToExam function...
      if (step?.stepData?.autoAdvance || step?.stepData?.autoAdvance === "true") {
        await redirectToExam();
      }
    };

    initLockdownOptions();
  }, []);


  const redirectToExam = async () => {
    // set the flag that we are preparing to redirect...
    setIsRedirecting(true);
    setHideTitle(true);

    // Record the start time to ensure minimum 2-second delay
    const startTime = Date.now();

    // First, we must call the API to set the session in progress
    await putSessionInProgress();

    // Tell the extension we are done with onboarding
    await sendMessageToExtension({
      message: 'sp_background__onboardingComplete',
    });

    // Then we need to enable the exam password button
    await updateToolbarButtonState(['examPassword'], true);

    // Then, we have to complete the current step
    await completeAndGetNextStep(step.journeyStepId);

    // Next, we need to make an annotation that Onboarding was complete
    await apiClient.addAnnotation({
      payload: {
        typeId: 'onboarding-complete',
        metadata: {
          redirectUrl: examRedirectUrl,
        },
      },
    });

    // Calculate how much time has elapsed
    const elapsedTime = Date.now() - startTime;

    // If less than MIN_REDIRECT_DELAY have passed, wait for the remaining time
    if (elapsedTime < MIN_REDIRECT_DELAY) {
      await new Promise(resolve => setTimeout(resolve, MIN_REDIRECT_DELAY - elapsedTime));
    }

    // Lastly, lets redirect this tab to the exam URL.
    window.location.replace(examRedirectUrl);
  };

  return (
    <SinglePane>
      <Flex
        direction="column"
        height="100%"
        justifyContent="center"
        alignItems="center"
        padding="medium"
      >
        {isRedirecting && (
          <Loader text={step?.stepData?.preparingToRedirectText || "Preparing to redirect to exam..."} />
        )}
        {!isRedirecting && examRedirectUrl && (
          <Flex direction="column">
            <StepInstructions
                  title="Instructions"
              instructions={step?.stepData?.body}
            />
            <Flex
              direction="column"
              height="100%"
              margin="medium"
              justifyItems="center"
              alignItems="center"
            >
              <Button
                color="primary"
                size="medium"
                onClick={redirectToExam}
                margin="medium 0 0 0"
              >
                {step?.stepData?.button}
              </Button>
            </Flex>
          </Flex>
        )}
        {!isRedirecting && !examRedirectUrl && (
          <Flex direction="column" alignItems="center">
            <Alert variant="error">{step?.stepData?.noRedirectUrlSet || 'No exam URL provided, please reach out to support.'}</Alert>
          </Flex>
        )}
      </Flex>
    </SinglePane>
  );
};

export default ExamRedirect;
